import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import produce from 'immer';
import axios from 'axios';
import deepmerge from 'deepmerge';
import axiosRetry from 'axios-retry';
import moment from 'moment-timezone';
import logger from '../common/logger';
import {
  getLearnLiveServiceHost,
  getLapLearnLiveServiceHost,
  convertToBoolean,
  adaptPicklistToCoach,
  adaptPicklistToChip,
  isNotEmpty,
  areObjectsEqual,
  getEnvironmentConstant,
} from '../common/utilities';
import { makeUpdateObject } from './commonStoreUtilities';
import {
  RETRY_COUNT,
  picklistNames,
  COACHING_TYPE_MAP,
  VIRTUAL_DIRECTORY,
  API_ENDPOINTS,
} from '../common/constants';

axiosRetry(axios, { retries: RETRY_COUNT });
const turnOnAccountFilter = true;
const initialState = {
  requestList: [],
  requestListCount: 0,
  requestCriteria: {},
  requestDetails: {},
  // also add the customer account picklists mock data in fullStateMock, in which the single items have key "type" and value is "customerAccount" -> which will be the key to the customer account data
  picklists: {},
  templates: [],
  requestListFilters: {
    selectedFilters: {},
  },
  status: {
    requestList: { isLoaderVisible: false, hasError: false, isLoaded: false },
    requestCriteria: { isLoaderVisible: false, hasError: false, isLoaded: false },
    requestDetails: { isLoaderVisible: false, hasError: false, isLoaded: false },
    picklists: { isLoaderVisible: false, hasError: false, isLoaded: false },
    templates: { isLoaderVisible: false, hasError: false, isLoaded: false },
    updateFields: {
      isLoaderVisible: false, hasError: false, isLoaded: false, updated: false, errorMessage: null,
    },
    changeFastTrackTemplate: {
      isLoaderVisible: false, hasError: false, isLoaded: false, errorMessage: null,
    },
    scheduleClass: {
      loadingItems: {},
    },
    contentItemAction: {
      loadingItems: {}, hasError: false, isLoaded: false, errorMessage: null,
    },
  },
};

// eslint-disable-next-line no-prototype-builtins
const findMissingKeys = (stringArray, object) => stringArray.filter((key) => !object.hasOwnProperty(key));

export const getCoachingTypeKeyById = (coachingTypeId) => (state) => {
  const obj = state?.coachingRequestManagementData?.picklists?.coachingTypes || {};
  const found = Object.values(obj).find((item) => item.id === coachingTypeId);
  return found ? found.key : null;
};

export const getIsClassCreationNotAllowed = (coachingRequestId) => (state) => {
  const coachingTypeId = state.coachingRequestManagementData?.requestDetails[
    coachingRequestId
  ]?.coachingType?.id;
  const coachingTypeKey = getCoachingTypeKeyById(coachingTypeId)(state);
  const canCreateClass = state.coachingRequestManagementData?.picklists?.coachingTypes?.[coachingTypeKey]?.canCreateClass;
  // Only allow class creation if canCreateClass is 1
  return parseInt(canCreateClass, 10) !== 1;
};

export const getCoachingRequestList = (state) => {
  const returnValue = state.coachingRequestManagementData.requestList;
  return returnValue;
};

export const getCoachingRequestListCount = (state) => {
  const returnValue = state.coachingRequestManagementData.requestListCount;
  return returnValue;
};

export const getCoachingRequestListByType = (coachingTypeId) => (state) => {
  logger.info('coachingTypeId', coachingTypeId);
  const data = state.coachingRequestManagementData.requestList;
  return data;
};

export const getIsCoachingRequestTypeProduct = (coachingRequestId) => (state) => {
  const data = state.coachingRequestManagementData?.requestDetails[
    coachingRequestId
  ]?.coachingType?.id;
  // Coaching Type Product is ID 1
  return parseInt(data, 10) === 1;
};

export const getCoachingRequestDetailsByRequestId = (coachingRequestId) => (state) => {
  const data = state?.coachingRequestManagementData?.requestDetails[coachingRequestId];
  return data;
};

export const getPicklists = (state) => {
  const returnValue = state.coachingRequestManagementData.picklists;
  return returnValue;
};

export const getTemplates = (state) => {
  const returnValue = state.coachingRequestManagementData.templates;
  return returnValue;
};

export const getDoThesePicklistsExist = (picklists) => (state) => {
  const returnValue = picklists.every((key) => key in state.coachingRequestManagementData.picklists);
  return returnValue;
};

export const getIsLoadingRequestDetail = (state) => convertToBoolean({ value: state?.coachingRequestManagementData?.status?.requestDetails?.isLoaderVisible });
export const getHasErrorRequestDetail = (state) => convertToBoolean({ value: state?.coachingRequestManagementData?.status?.requestDetails?.hasError });
export const getIsLoadingScheduleClass = (itemId) => (state) => convertToBoolean({ value: state?.coachingRequestManagementData?.status?.scheduleClass?.loadingItems?.[itemId]?.isLoaderVisible });
export const getIsLoadingRequestList = (state) => convertToBoolean({ value: state?.coachingRequestManagementData?.status?.requestList?.isLoaderVisible });
export const getIsLoadingUpdateField = (state) => convertToBoolean({ value: state?.coachingRequestManagementData?.status?.updateFields?.isLoaderVisible });
export const getIsLoadingChangeFastTrackTemplate = (state) => convertToBoolean({ value: state?.coachingRequestManagementData?.status?.changeFastTrackTemplate?.isLoaderVisible });
export const getHasErrorChangeFastTrackTemplate = (state) => convertToBoolean({ value: state?.coachingRequestManagementData?.status?.changeFastTrackTemplate?.hasError });
export const getChangeFastTrackTemplateErrorMessage = (state) => state?.coachingRequestManagementData?.status?.changeFastTrackTemplate?.errorMessage;
export const getIndustrySubSegments = (state) => adaptPicklistToChip({ data: state?.coachingRequestManagementData.picklists.industrySubSegments || [] });
export const getCapabilities = (state) => adaptPicklistToChip({ data: state?.coachingRequestManagementData.picklists.capabilities || [] });
export const getHasUpdateFieldsError = (state) => convertToBoolean({ value: state?.coachingRequestManagementData?.status?.updateFields?.hasError });
export const getUpdateFieldsErrorMessage = (state) => state?.coachingRequestManagementData?.status?.updateFields?.errorMessage;
/**
 * Selector to get customer account options for the filters.
 *
 * New API: customerCompanies comes back as an array of objects:
 *   { CustomerCompany: "adsk", coachingTypeId: 1 }
 *
 * - We only want to show a customer company in the dropdown for the
 *   matching coaching request type (e.g. coachingTypeId === 1 for Product).
 * - For backwards compatibility (tests / older mocks), this selector
 *   also supports the legacy array-of-strings shape.
 *
 * Usage (preferred / new):
 *   useSelector(getCustomerAccounts(coachingTypeId))
 *
 * Legacy (direct state) usage is still supported for tests:
 *   getCustomerAccounts(state)
 */
export const getCustomerAccounts = (maybeCoachingTypeIdOrState) => {
  // Backwards-compatible behaviour: if called as getCustomerAccounts(state)
  // (i.e. argument already looks like Redux state), keep the old logic.
  if (maybeCoachingTypeIdOrState
    && typeof maybeCoachingTypeIdOrState === 'object'
    && maybeCoachingTypeIdOrState.coachingRequestManagementData) {
    const state = maybeCoachingTypeIdOrState;
    const arr = state?.coachingRequestManagementData.picklists[picklistNames.customerAccount] || [];
    const legacyReturnValue = arr.map((theString) => ({ id: theString, label: theString }));
    if (!turnOnAccountFilter) {
      return [];
    }
    return legacyReturnValue;
  }

  // New curried selector usage: getCustomerAccounts(coachingTypeId)(state)
  const coachingTypeId = maybeCoachingTypeIdOrState;

  return (state) => {
    const rawArray = state?.coachingRequestManagementData.picklists[picklistNames.customerAccount] || [];

    if (!turnOnAccountFilter) {
      return [];
    }

    // Normalise data so that we can handle both:
    // - legacy: ['Account1', 'Account2']
    // - new: [{ CustomerCompany: 'adsk', coachingTypeId: 1 }, ...]
    const normalised = rawArray.map((item) => {
      if (typeof item === 'string') {
        // Legacy string entry – no coachingTypeId information, so leave it undefined
        return {
          company: item,
          coachingTypeId: undefined,
        };
      }

      const company = item.CustomerCompany
        || item.customerCompany
        || item.company
        || item.label
        || '';

      return {
        company,
        coachingTypeId: item.coachingTypeId,
      };
    });

    // If a coachingTypeId is provided, only show companies for that type.
    // Items without a coachingTypeId will be included for all types.
    const filteredByType = coachingTypeId
      ? normalised.filter((item) => item.coachingTypeId === coachingTypeId
        || item.coachingTypeId === undefined
        || item.coachingTypeId === null)
      : normalised;

    const options = filteredByType
      .filter((item) => item.company)
      .map((item) => ({
        id: item.company,
        label: item.company,
      }));

    return options;
  };
};
export const getCoachingSubTypes = (RequestType) => (state) => adaptPicklistToChip({
  data: state?.coachingRequestManagementData?.picklists?.coachingTypes
    ? state?.coachingRequestManagementData?.picklists?.coachingTypes[RequestType]?.coachingSubTypes
    : [],
});
export const getAdjacentCoachingRequestIds = (targetId) => (state) => {
  const data = state.coachingRequestManagementData.requestList;
  const ids = data.map((item) => item.coachingRequestId.toString());
  const index = ids.indexOf(targetId);
  if (index === -1) {
    return null;
  }
  const prevId = index > 0 ? ids[index - 1] : undefined;
  const nextId = index < ids.length - 1 ? ids[index + 1] : undefined;
  return { prev: prevId, next: nextId };
};
export const getOutcomes = (state) => adaptPicklistToChip({ data: state?.coachingRequestManagementData.picklists.outcomes || [] });
export const getCoachListAsOptions = (coachingTypeIds = []) => (state) => {
  if (state?.coachingRequestManagementData?.picklists && Object.keys(state.coachingRequestManagementData.picklists).length > 0 && state.coachingRequestManagementData.picklists.coach) {
    if (coachingTypeIds.length > 0) {
      return adaptPicklistToCoach({ data: state?.coachingRequestManagementData.picklists.coach.filter((item) => (coachingTypeIds.includes(item.coachingTypeId))) });
    }
    return adaptPicklistToCoach({ data: state?.coachingRequestManagementData.picklists.coach });
  }
  return [];
};
export const getChipList = (coachingTypeIds = []) => (state) => {
  if (state?.coachingRequestManagementData?.picklists && Object.keys(state.coachingRequestManagementData.picklists).length > 0 && state.coachingRequestManagementData.picklists.status) {
    if (coachingTypeIds.length > 0) {
      return adaptPicklistToChip({ data: state?.coachingRequestManagementData.picklists.status.filter((item) => (coachingTypeIds.includes(item.coachingTypeId))) });
    }
    return adaptPicklistToChip({ data: state?.coachingRequestManagementData.picklists.status });
  }
  return [];
};
export const getCancellationReasons = (coachingTypeId) => (state) => {
  if (state?.coachingRequestManagementData?.picklists?.cancellationReasons) {
    const { cancellationReasons } = state.coachingRequestManagementData.picklists;

    if (coachingTypeId) {
      return cancellationReasons.filter((item) => item.coachingTypeId === coachingTypeId);
    }
    return cancellationReasons;
  }

  return [];
};
export const getHasErrorRequestList = (state) => convertToBoolean({ value: state?.coachingRequestManagementData?.status?.requestList?.hasError });
export const getIsRequestViewOnly = (coachingRequestId) => (state) => convertToBoolean({ value: isNotEmpty(state?.coachingRequestManagementData?.requestDetails[coachingRequestId]?.classId) });
export const getIsListRequestViewOnly = (coachingRequestId) => (state) => convertToBoolean({
  value: isNotEmpty(
    state?.coachingRequestManagementData?.requestList?.find(
      (req) => req.coachingRequestId === coachingRequestId,
    )?.classId,
  ),
});
export const getRequestListFilters = (state) => state?.coachingRequestManagementData?.requestListFilters;
// Fast Track specific selectors (for backwards compatibility)
export const getFastTrackRequestList = (state) => state.coachingRequestManagementData.requestList;
export const getFastTrackRequestListCount = (state) => state.coachingRequestManagementData.requestListCount;
export const getFastTrackRequestDetails = (requestId) => (state) => state.coachingRequestManagementData.requestDetails[requestId];
export const getFastTrackPicklists = (state) => state.coachingRequestManagementData.picklists;
export const getFastTrackRequestListFilters = (state) => state.coachingRequestManagementData.requestListFilters;
export const getIsFastTrackLoadingRequestList = (state) => convertToBoolean({ value: state?.coachingRequestManagementData?.status?.requestList?.isLoaderVisible });
export const getHasFastTrackErrorRequestList = (state) => convertToBoolean({ value: state?.coachingRequestManagementData?.status?.requestList?.hasError });
export const getFastTrackStatusOptions = (state) => adaptPicklistToChip({ data: state?.coachingRequestManagementData?.picklists?.status?.filter((item) => item.coachingTypeId === 5) || [] });
export const getFastTrackLeadOptions = (state) => adaptPicklistToCoach({
  data: state?.coachingRequestManagementData?.picklists?.coach?.filter(
    (coach) => coach.coachingTypeId === 5 && coach.isLead,
  ) || [],
});
export const getFastTrackCoachOptions = (state) => adaptPicklistToCoach({
  data: state?.coachingRequestManagementData?.picklists?.coach?.filter(
    (coach) => coach.coachingTypeId === 5,
  ) || [],
});
export const getIsFieldUpdating = (state) => convertToBoolean({ value: state?.coachingRequestManagementData?.status?.updateFields?.isLoaderVisible });
export const getIsFieldUpdated = (state) => convertToBoolean({ value: state?.coachingRequestManagementData?.status?.updateFields?.updated === true });
export const getIsContentItemActionLoading = (itemId) => (state) => convertToBoolean({ value: state?.coachingRequestManagementData?.status?.contentItemAction?.loadingItems?.[itemId] });
export const getHasContentItemActionError = (state) => convertToBoolean({ value: state?.coachingRequestManagementData?.status?.contentItemAction?.hasError });
export const getContentItemActionErrorMessage = (state) => state?.coachingRequestManagementData?.status?.contentItemAction?.errorMessage;

export const getUpdateFieldsStatus = (state) => state?.coachingRequestManagementData?.status?.updateFields;
// Selector to get the coaching type map merged with the constant
export const getMergedCoachingTypeMap = (state) => {
  const dbTypes = state.coachingRequestManagementData.picklists?.coachingTypes;
  const picklistStatus = state.coachingRequestManagementData.status.picklists;

  // If loading and not errored, return empty map (show nothing)
  if (picklistStatus.isLoaderVisible && !picklistStatus.hasError) {
    return {};
  }

  // If fetch succeeded and dbTypes is present, merge and return
  if (!picklistStatus.isLoaderVisible && !picklistStatus.hasError && dbTypes && typeof dbTypes === 'object' && Object.keys(dbTypes).length > 0) {
    return Object.keys(COACHING_TYPE_MAP).reduce((acc, key) => ({
      ...acc,
      [key]: {
        ...COACHING_TYPE_MAP[key],
        ...(dbTypes[key] || {}),
      },
    }), {});
  }

  // If fetch failed, return the default map
  if (picklistStatus.hasError) {
    return COACHING_TYPE_MAP;
  }

  // Default: return empty map (shouldn't happen, but safe fallback)
  return {};
};

export const fetchCoachingRequestList = createAsyncThunk(
  'fetchCoachingRequestList',
  async ({
    env, token, pageObject, sortObject, filterObject,
  }, { rejectWithValue }) => {
    const headers = {
      'Content-Type': 'application/json',
      Authorization: token,
    };

    const coachingRequestListUrl = `${getLearnLiveServiceHost({ env })}/coachingrequest/list/v1`;

    const body = {
      pageSize: pageObject.pageSize,
      pageNumber: pageObject.pageNumber,
      filterCriteria: filterObject,
    };
    if (isNotEmpty(sortObject?.sortBy)) {
      body.sortCriteria = [
        {
          fieldName: sortObject.sortBy,
          order: sortObject.orderBy,
        },
      ];
    }
    try {
      const response = await axios.post(coachingRequestListUrl, body, { headers: { ...headers } });
      // update status label for fast track requests
      if (filterObject.some((filter) => filter.fieldName === 'CoachingRequestTypeId' && filter.values.includes(5))) {
        const requests = (response?.data?.coachingRequests ?? []).map((item) => {
          if (item?.status?.id === 4) {
            return {
              ...item,
              status: {
                id: 4,
                label: 'Complete',
              },
            };
          }
          return item;
        });
        response.data.coachingRequests = requests;
      }
      return response.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const fetchPicklists = createAsyncThunk(
  'fetchPicklists',
  async ({ env, token, picklists }, { getState, rejectWithValue }) => {
    if (Array.isArray(picklists) && picklists.length) {
      const headers = {
        'Content-Type': 'application/json',
        Authorization: token,
      };
      const picklistsInStore = getPicklists(getState());
      const picklistsToFetch = findMissingKeys(picklists, picklistsInStore);
      if (picklistsToFetch.length) {
        const picklistsRequestUrl = `${getLearnLiveServiceHost({ env })}/coachingrequest/picklist/v1?options=${picklistsToFetch.join(',')}`;
        try {
          const response = await axios.get(picklistsRequestUrl, { headers });
          return response.data;
        } catch (err) {
          return rejectWithValue(err);
        }
      } else {
        return rejectWithValue(new Error('no picklist keys to fetch that have not been fetched yet.'));
      }
    }

    return rejectWithValue(new Error('no picklists passed into picklist call'));
  },
);

export const fetchCoachingRequestDetails = createAsyncThunk(
  'fetchCoachingRequestDetails',
  async ({ env, token, coachingRequestId }, { rejectWithValue }) => {
    const headers = {
      'Content-Type': 'application/json',
      Authorization: token,
    };
    const coachingRequestDetailUrl = `${getLearnLiveServiceHost({ env })}/coachingrequest/details/v1/${coachingRequestId}`;
    try {
      const response = await axios
        .get(coachingRequestDetailUrl, {
          headers: {
            ...headers,
          },
        });
      if (response?.data?.coachingType?.id === 5 && response?.data?.status?.id === 4) {
        response.data.status.label = 'Complete';
      }
      return response.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const updateFields = createAsyncThunk(
  'model/updateFields',
  async ({
    env, token, updatedFields, model,
  }, { rejectWithValue }) => {
    const cleanedUpdatedFields = updatedFields.map((field) => ({
      ...field,
      fieldData: {
        ...field.fieldData,
        updateObject: undefined,
      },
    }));

    const headers = {
      'Content-Type': 'application/json',
      Authorization: token,
    };
    const serviceUrl = `${getLearnLiveServiceHost({ env })}/coachingrequest/edit/v1`;
    const body = makeUpdateObject(updatedFields);
    try {
      const response = await axios
        .post(serviceUrl, body, { headers: { ...headers } });
      if (response.status === 200) {
        return { updatedFields: cleanedUpdatedFields, model };
      }
      return null;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const scheduleClass = createAsyncThunk(
  'scheduleClass',
  async ({
    env, token, item, coachingRequestDetails,
  }, { rejectWithValue }) => {
    try {
      // First, create the class
      const createClassPayload = {
        instructorEmail: coachingRequestDetails?.coach?.email || '',
        startDate: coachingRequestDetails?.deliveryDate ? moment.utc(coachingRequestDetails.deliveryDate).format('YYYY-MM-DDTHH:MM') : '',
        endDate: coachingRequestDetails?.classEndDate ? moment.utc(coachingRequestDetails.classEndDate).format('YYYY-MM-DDTHH:MM') : '',
        csn: coachingRequestDetails?.accountCSN || '',
        requestId: coachingRequestDetails?.coachingRequestId || null,
        authToken: token,
        courseId: item.id,
        rootResourceId: coachingRequestDetails?.createdEntityId,
      };

      const createClassHeaders = {
        'Content-Type': 'application/json',
      };

      const createClassResponse = await axios.post(
        `${getEnvironmentConstant({ key: VIRTUAL_DIRECTORY, env })}/api/createClass/v1`,
        createClassPayload,
        { headers: createClassHeaders },
      );

      const classId = createClassResponse?.data?.id;
      if (!classId) {
        throw new Error('Failed to get class ID from create class response');
      }

      // Return both the class ID and environment for URL generation
      return { classId, env };
    } catch (error) {
      logger.error('Error in scheduleClass:', error);
      return rejectWithValue(error);
    }
  },
);

export const fetchFastTrackTemplates = createAsyncThunk(
  'fetchFastTrackTemplates',
  async ({ env, token }, { rejectWithValue }) => {
    const headers = {
      'Content-Type': 'application/json',
      Authorization: token,
    };
    const templatesUrl = `${getLearnLiveServiceHost({ env })}/coachingrequest/picklist/v1?options=fastTrackTemplates`;
    try {
      const response = await axios.get(templatesUrl, { headers });
      return response?.data?.fastTrackTemplates || [];
    } catch (error) {
      logger.error('Error fetching FastTrack templates:', error);
      return rejectWithValue(error?.response?.data || error.message);
    }
  },
);

export const changeFastTrackTemplate = createAsyncThunk(
  'changeFastTrackTemplate',
  async ({
    env,
    token,
    planId,
    newFastTrackId,
  }, { rejectWithValue }) => {
    const headers = {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    };
    const changeUrl = `${getLapLearnLiveServiceHost({ env })}/v1/plan/${planId}/change-fasttrack`;
    const body = {
      newFastTrackId,
    };
    try {
      const response = await axios.put(changeUrl, body, { headers });
      if (response.status === 200) {
        return { planId, newFastTrackId, data: response.data };
      }
      return null;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const contentItemAction = createAsyncThunk(
  'contentItemAction',
  async ({
    env,
    token,
    planId,
    itemId,
    action,
  }, { rejectWithValue }) => {
    const headers = {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    };
    const actionUrl = `${getLapLearnLiveServiceHost({
      env,
    })}/${API_ENDPOINTS.CONTENT_ITEM_ACTION}/${planId}/${itemId}`;
    const body = {
      action,
    };
    try {
      const response = await axios.patch(actionUrl, body, { headers });
      if (response.status >= 200 && response.status < 300) {
        return {
          planId, itemId, action, data: response.data,
        };
      }
      return null;
    } catch (error) {
      return rejectWithValue({
        message: error.message,
        status: error.response?.status,
      });
    }
  },
);

const coachingRequestManagementSlice = createSlice({
  name: 'coachingRequestManagementSlice',
  initialState,
  reducers: {
    addFilters: (state, action) => {
      const { val, selectType } = action.payload;
      const newState = produce(state, (draftState) => {
        draftState.requestListFilters.selectedFilters[selectType] = val;
      });
      return newState;
    },
    removeFilter: (state, action) => {
      const { itemToRemove } = action.payload;

      const newState = produce(state, (draftState) => {
        Object.keys(draftState.requestListFilters.selectedFilters).forEach((key) => {
          draftState.requestListFilters.selectedFilters[key] = draftState.requestListFilters.selectedFilters[key].filter(
            (item) => !areObjectsEqual(item, itemToRemove),
          );
        });
      });

      return newState;
    },

    clearFilters: (state) => {
      const newState = produce(state, (draftState) => {
        // Reset selectedFilters to empty object to ensure clean state
        draftState.requestListFilters.selectedFilters = {};
      });
      return newState;
    },

    clearChangeFastTrackTemplateError: (state) => {
      const newState = produce(state, (draftState) => {
        draftState.status.changeFastTrackTemplate.hasError = false;
        draftState.status.changeFastTrackTemplate.errorMessage = null;
      });
      return newState;
    },

    clearContentItemActionError: (state) => {
      const newState = produce(state, (draftState) => {
        draftState.status.contentItemAction.hasError = false;
        draftState.status.contentItemAction.errorMessage = null;
      });
      return newState;
    },

    setContentItemLoading: (state, action) => {
      const { itemId, isLoading } = action.payload;
      const newState = produce(state, (draftState) => {
        if (isLoading) {
          draftState.status.contentItemAction.loadingItems[itemId] = true;
        } else {
          delete draftState.status.contentItemAction.loadingItems[itemId];
        }
      });
      return newState;
    },
    updateContentItemStatus: (state, action) => {
      const { coachingRequestId, itemId, isRemoved } = action.payload;
      const newState = produce(state, (draftState) => {
        const requestDetails = draftState.requestDetails[coachingRequestId];
        if (requestDetails && requestDetails.createdEntity && requestDetails.createdEntity.sections) {
          // Find and update the specific content item
          requestDetails.createdEntity.sections.forEach((section) => {
            if (section.contentItems) {
              const item = section.contentItems.find((contentItem) => contentItem.id === itemId);
              if (item) {
                item.isRemoved = isRemoved;
              }
            }
          });
        }
      });
      return newState;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchCoachingRequestList.pending, (state) => {
      const newState = produce(state, (draftState) => {
        draftState.status.requestList.isLoaderVisible = true;
        return draftState;
      });
      return newState;
    });
    builder.addCase(fetchCoachingRequestList.fulfilled, (state, action) => {
      const payload = action?.payload || {};
      if (!payload.error) {
        const newState = produce(state, (draftState) => {
          draftState.requestList = payload?.coachingRequests;
          draftState.requestListCount = payload?.coachingRequestsCount;
          draftState.status.requestList.isLoaderVisible = false;
        });
        return newState;
      }
      return state;
    });
    builder.addCase(fetchCoachingRequestList.rejected, (state, action) => {
      logger.error('Error in fetchCoachingRequestList: ', action.payload);
      const newState = produce(state, (draftState) => {
        draftState.requestList = [];
        draftState.requestListCount = 0;
        draftState.status.requestList.isLoaderVisible = false;
        draftState.status.requestList.hasError = true;
      });
      return newState;
    });

    builder.addCase(fetchCoachingRequestDetails.pending, (state) => {
      const newState = produce(state, (draftState) => {
        draftState.status.requestDetails.isLoaderVisible = true;
        return draftState;
      });
      return newState;
    });
    builder.addCase(fetchCoachingRequestDetails.fulfilled, (state, action) => {
      const data = action?.payload || {};
      if (!data.error) {
        const newState = produce(state, (draftState) => {
          draftState.requestDetails[action.payload?.coachingRequestId] = data;
          draftState.status.requestDetails.isLoaderVisible = false;
        });
        return newState;
      }
      return state;
    });
    builder.addCase(fetchCoachingRequestDetails.rejected, (state, action) => {
      logger.error('Error in fetchCoachingRequestDetails: ', action.payload);
      const newState = produce(state, (draftState) => {
        draftState.requestDetails[action.payload?.coachingRequestId] = undefined;
        draftState.status.requestDetails.isLoaderVisible = false;
        draftState.status.requestDetails.hasError = true;
      });
      return newState;
    });

    builder.addCase(fetchPicklists.pending, (state) => {
      const newState = produce(state, (draftState) => {
        draftState.status.picklists.isLoaderVisible = true;
        return draftState;
      });
      return newState;
    });
    builder.addCase(fetchPicklists.fulfilled, (state, action) => {
      const data = action?.payload || {};
      if (!data.error) {
        const newState = produce(state, (draftState) => {
          draftState.picklists = deepmerge(state?.picklists, data);
          draftState.status.picklists.isLoaderVisible = false;
        });
        return newState;
      }
      return state;
    });
    builder.addCase(fetchPicklists.rejected, (state, action) => {
      logger.error('Error in fetchPicklists: ', action.payload);
      const newState = produce(state, (draftState) => {
        draftState.status.picklists.isLoaderVisible = false;
        draftState.status.picklists.hasError = true;
      });
      return newState;
    });
    builder.addCase(updateFields.pending, (state) => {
      const newState = produce(state, (draftState) => {
        draftState.status.updateFields.isLoaderVisible = true;
        draftState.status.updateFields.errorMessage = null;
        return draftState;
      });
      return newState;
    });
    builder.addCase(updateFields.fulfilled, (state, action) => {
      const data = action?.payload || [];
      if (!data.error) {
        const newState = produce(state, (draftState) => {
          action.payload?.updatedFields.forEach((field) => {
            const keys = field.fieldData.updateField.split('.');
            if (action.payload?.model !== 'list') {
              if (keys.length === 1) {
                draftState.requestDetails[field.fieldData.coachingRequestId][keys[0]] = field.newValue.value;
              } else if (keys.length === 2) {
                draftState.requestDetails[field.fieldData.coachingRequestId][keys[0]][keys[1]] = field.newValue.value;
              } else if (keys.length === 3) {
                draftState.requestDetails[field.fieldData.coachingRequestId][keys[0]][keys[1]][keys[2]] = field.newValue.value;
              } else {
                throw new Error('updateFields failed: no matching field to update.');
              }
            } else {
              const index = draftState.requestList.findIndex((req) => req.coachingRequestId === field.fieldData.coachingRequestId);
              // Check if the object and field exist
              if (index !== -1 && keys[0] in draftState.requestList[index]) {
                // Update the field with the new value
                draftState.requestList[index][keys[0]] = field.newValue.value;
              } else {
                throw new Error('Invalid coachingRequestId or fieldName');
              }
            }
          });
          draftState.status.updateFields.isLoaderVisible = false;
          draftState.status.updateFields.isLoaded = true;
          draftState.status.updateFields.updated = true;
          draftState.status.updateFields.errorMessage = null;
        });
        return newState;
      }
      return state;
    });
    builder.addCase(updateFields.rejected, (state, action) => {
      logger.error('Error in updateFields: ', action.payload);
      const newState = produce(state, (draftState) => {
        draftState.status.updateFields.isLoaderVisible = false;
        draftState.status.updateFields.hasError = true;
        // Try to extract error message from headers first, then fallback to response body
        draftState.status.updateFields.errorMessage = action.payload?.response?.headers?.['X-LearnLive-Api-Error']
          || action.payload?.response?.headers?.['x-learnlive-api-error']
          || action.payload?.response?.data?.message
          || action.payload?.message
          || 'An error occurred while updating fields';
      });
      return newState;
    });
    builder.addCase(scheduleClass.pending, (state, action) => {
      const itemId = action.meta?.arg?.item?.id;
      const newState = produce(state, (draftState) => {
        if (itemId) {
          draftState.status.scheduleClass.loadingItems[itemId] = {
            isLoaderVisible: true,
            hasError: false,
            classId: null,
          };
        }
        return draftState;
      });
      return newState;
    });
    builder.addCase(scheduleClass.fulfilled, (state, action) => {
      const data = action?.payload || {};
      const itemId = action.meta?.arg?.item?.id;
      if (!data.error && itemId) {
        const newState = produce(state, (draftState) => {
          draftState.status.scheduleClass.loadingItems[itemId] = {
            isLoaderVisible: false,
            hasError: false,
            classId: data.classId,
          };
        });
        return newState;
      }
      return state;
    });
    builder.addCase(scheduleClass.rejected, (state, action) => {
      logger.error('Error in scheduleClass: ', action.payload);
      const itemId = action.meta?.arg?.item?.id;
      const newState = produce(state, (draftState) => {
        if (itemId) {
          draftState.status.scheduleClass.loadingItems[itemId] = {
            isLoaderVisible: false,
            hasError: true,
            classId: null,
          };
        }
      });
      return newState;
    });
    // fetchFastTrackTemplates reducers
    builder.addCase(fetchFastTrackTemplates.pending, (state) => {
      const newState = produce(state, (draftState) => {
        draftState.status.templates.isLoaderVisible = true;
        draftState.status.templates.hasError = false;
        return draftState;
      });
      return newState;
    });
    builder.addCase(fetchFastTrackTemplates.fulfilled, (state, action) => {
      const data = action?.payload || [];
      const newState = produce(state, (draftState) => {
        draftState.templates = data;
        draftState.status.templates.isLoaderVisible = false;
        draftState.status.templates.isLoaded = true;
        return draftState;
      });
      return newState;
    });
    builder.addCase(fetchFastTrackTemplates.rejected, (state, action) => {
      logger.error('Error in fetchFastTrackTemplates: ', action.payload);
      const newState = produce(state, (draftState) => {
        draftState.status.templates.isLoaderVisible = false;
        draftState.status.templates.hasError = true;
      });
      return newState;
    });

    // changeFastTrackTemplate reducers
    builder.addCase(changeFastTrackTemplate.pending, (state) => {
      const newState = produce(state, (draftState) => {
        draftState.status.changeFastTrackTemplate.isLoaderVisible = true;
        draftState.status.changeFastTrackTemplate.hasError = false;
        draftState.status.changeFastTrackTemplate.errorMessage = null;
        return draftState;
      });
      return newState;
    });
    builder.addCase(changeFastTrackTemplate.fulfilled, (state) => {
      const newState = produce(state, (draftState) => {
        draftState.status.changeFastTrackTemplate.isLoaderVisible = false;
        draftState.status.changeFastTrackTemplate.isLoaded = true;
        draftState.status.changeFastTrackTemplate.hasError = false;
        draftState.status.changeFastTrackTemplate.errorMessage = null;
        return draftState;
      });
      return newState;
    });
    builder.addCase(changeFastTrackTemplate.rejected, (state, action) => {
      logger.error('Error in changeFastTrackTemplate: ', action.payload);
      const newState = produce(state, (draftState) => {
        draftState.status.changeFastTrackTemplate.isLoaderVisible = false;
        draftState.status.changeFastTrackTemplate.hasError = true;
        draftState.status.changeFastTrackTemplate.errorMessage = action.payload?.response?.data?.message || action.payload?.message || 'An error occurred while changing the template';
      });
      return newState;
    });

    // contentItemAction reducers
    builder.addCase(contentItemAction.pending, (state, action) => {
      const { itemId } = action.meta.arg;
      const newState = produce(state, (draftState) => {
        draftState.status.contentItemAction.loadingItems[itemId] = true;
        draftState.status.contentItemAction.hasError = false;
        draftState.status.contentItemAction.errorMessage = null;
      });
      return newState;
    });
    builder.addCase(contentItemAction.fulfilled, (state, action) => {
      const { itemId } = action.meta.arg;
      const newState = produce(state, (draftState) => {
        delete draftState.status.contentItemAction.loadingItems[itemId];
        draftState.status.contentItemAction.isLoaded = true;
        draftState.status.contentItemAction.hasError = false;
        draftState.status.contentItemAction.errorMessage = null;
      });
      return newState;
    });
    builder.addCase(contentItemAction.rejected, (state, action) => {
      const { itemId } = action.meta.arg;
      logger.error('Error in contentItemAction: ', action.payload);
      const newState = produce(state, (draftState) => {
        delete draftState.status.contentItemAction.loadingItems[itemId];
        draftState.status.contentItemAction.hasError = true;
        draftState.status.contentItemAction.errorMessage = action.payload?.message || 'Something went wrong';
      });
      return newState;
    });
  },
});

export const {
  addFilters,
  removeFilter,
  clearFilters,
  clearChangeFastTrackTemplateError,
  clearContentItemActionError,
  setContentItemLoading,
  updateContentItemStatus,
} = coachingRequestManagementSlice.actions;

// Fast Track specific action aliases (for backwards compatibility)
export const addFastTrackFilters = addFilters;
export const removeFastTrackFilter = removeFilter;
export const clearFastTrackFilters = clearFilters;
export const fetchFastTrackRequestList = fetchCoachingRequestList;
export const fetchFastTrackRequestDetails = fetchCoachingRequestDetails;
export const fetchFastTrackPicklists = fetchPicklists;
export const updateFastTrackFields = updateFields;

export default coachingRequestManagementSlice.reducer;
